import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {OtpRecipient, OtpScreen, OtpValidateTokenResponse} from './otp';
import {throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {OtpErrorCode, OtpErrorDefinition} from './otp-error-defnition';

@Injectable()
export class OtpService {

  public initialLoading = true;
  public sending: boolean = false;
  public verifying: boolean = false;

  public currentScreen: OtpScreen = OtpScreen.SEND;

  public passed: boolean = false;

  public minutesToUnlock: number = 0;
  public remainingAttempts: number = 5;

  public selectedRecipient: OtpRecipient = null;
  public otpRecipients: OtpRecipient[] = [];

  public otpCodeLength: number = 6;
  public otpCode: string = '';

  public error: OtpErrorDefinition;

  constructor(private http: HttpClient) {
  }

  public initialise(): void {
    if (this.error?.fatalError) {
      this.initialLoading = false;
      return;
    }
    this.getOtpRecipients();
  }

  public setErrorMessage(errorCode: string): void {
    if (errorCode == OtpErrorCode.REJECTED) {
      if (this.remainingAttempts < 4 ) {
        errorCode = OtpErrorCode.REJECTEDMULTIPLE;
      }
    }
    this.error = OtpErrorDefinition.for(errorCode);
  }

  public clearErrorMessage(): void {
    this.error = null;
  }

  private getOtpRecipients(): void {
    this.initialLoading = true;
    this.clearErrorMessage();
    this.http.get('api/otp/recipients').pipe(
      catchError((response: HttpErrorResponse) => {
        return throwError(response.statusText);
      })).subscribe((otpRecipients: OtpRecipient[]) => {
        this.otpRecipients = otpRecipients;
        if (!otpRecipients.length) {
          this.setErrorMessage(OtpErrorCode.NORECIPIENTS);
        }
        this.initialLoading = false;
      },
      () => {
        this.setErrorMessage(OtpErrorCode.SYSTEMERROR);
        this.initialLoading = false;
      }
    );
  }

  public sendOtpSms(otpRecipient: OtpRecipient): void {
    this.sendOtp(otpRecipient, 'sms');
  }

  private sendOtp(otpRecipient: OtpRecipient, type): void {
    this.sending = true;
    this.clearErrorMessage();
    this.selectedRecipient = null;
    this.http.post('api/otp/' + type + '/' + otpRecipient.contactId, null).pipe(
      catchError((response: HttpErrorResponse) => {
        return throwError(response.statusText);
      })).subscribe(
      () => {
        this.selectedRecipient = otpRecipient;
        this.currentScreen = OtpScreen.VERIFY;
        this.sending = false;
      },
      () => {
        this.setErrorMessage(OtpErrorCode.SYSTEMERROR);
        this.sending = false;
      }
    );
  }

  public validateOtpSms(callback?: (response : OtpValidateTokenResponse) => any): any {
    return this.validateOtp('sms', callback);
  }

  private validateOtp(type: string, callback?: (response : OtpValidateTokenResponse) => any): any {
    if (!this.selectedRecipient || !this.otpCode) {
      return callback && callback(null);
    }
    this.verifying = true;
    this.clearErrorMessage();
    const otpCode = encodeURIComponent(this.otpCode);
    this.http.post('api/otp/' + type + '/' + this.selectedRecipient.contactId + '/' + otpCode, null).pipe(
      catchError((response: HttpErrorResponse) => {
        return throwError(response.statusText);
      })).subscribe(
      (otpValidateTokenResponse: OtpValidateTokenResponse) => {
        if (otpValidateTokenResponse.responseCode == 'PASSED') {
          this.passed = true;
        } else {
          this.passed = false;
          this.remainingAttempts = otpValidateTokenResponse.remainingAttempts;
          this.setErrorMessage(otpValidateTokenResponse.responseCode);
          if (otpValidateTokenResponse.responseCode === OtpErrorCode.EXPIRED) {
            this.otpCode = '';
          }
        }
        this.verifying = false;
        return callback && callback(otpValidateTokenResponse);
      },
      () => {
        this.setErrorMessage(OtpErrorCode.SYSTEMERROR);
        this.verifying = false;
        return callback && callback(null);
      }
    );
  }

}
